// The History card of the Temp and Vent tabs: range control (3h … 1y), series chips that toggle lines and
// show the value at the cursor, and one Highcharts chart with stacked panes on a shared x axis. Hover / touch
// drives the readout (no floating tooltip); drag / pinch zooms, with our own "Reset zoom".
import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import * as Highcharts from 'highcharts';

import { LayoutSize } from '../util';
import { weekdayTime } from '../util/format';
import { HISTORY_RANGES, HistoryRange, fitAxis, historyTicks, longRange } from '../util/history';
import { Point } from '../util/rrd';
import { Card } from './ui/card';
import {
  ChartView,
  Crosshair,
  PaneTitle,
  TALLINN_TIME,
  ZoomHandlers,
  hideHighchartsResetZoom,
  nudgeEdgeLabels,
  paneAxis,
  useChartPointer,
  useWindowZoom,
} from './ui/chart';
import { ResetZoomButton, Segmented } from './ui/controls';
import { HAIRLINE_STRONG, INK_SOFT, MUTED } from './ui/tokens';

// Series removed from the options are removed from the chart (a sensor without data for a range).
const UPDATE_ONE_TO_ONE: [boolean, boolean, boolean] = [true, true, true];

export interface HistoryPane {
  key: string;
  height: Record<LayoutSize, number>;
  title?: string;
  // The pane's y axis (position, label style and id are added).
  axis: Highcharts.YAxisOptions;
}

export interface HistorySeries {
  id: string;
  pane: string;
  data: Point[];
  color: string;
  visible?: boolean;
  type?: 'line' | 'area';
  step?: 'left' | 'right';
  lineWidth?: number;
  fillOpacity?: number;
}

// A y axis fitted to the visible series (nice steps, at least `minStep`; see fitAxis), with at most one label
// per 14 px of pane height so short panes stay readable.
export function fittedAxis(minStep: number): Highcharts.YAxisOptions {
  return {
    startOnTick: true,
    endOnTick: true,
    tickPositioner() {
      const { dataMin, dataMax } = this.getExtremes();
      const { height } = this.options as Highcharts.YAxisOptions;
      const px = typeof height === 'number' ? height : 100; // the pane's height
      const maxTicks = Math.max(3, Math.floor(px / 14));
      return typeof dataMin === 'number' && typeof dataMax === 'number'
        ? fitAxis(dataMin, dataMax, minStep, maxTicks).ticks
        : [0, 10, 20];
    },
  };
}

interface Geometry {
  left: number;
  font: number;
  height: number;
  panes: Map<string, { top: number; height: number }>;
}

const BOTTOM = 20; // x labels

// Highcharts ids are shared by axes and series: a pane "level" and a series "level" would collide.
const paneId = (key: string) => `pane-${key}`;

function geometry(size: LayoutSize, panes: HistoryPane[]): Geometry {
  const gap = size === 'phone' ? 8 : 10;
  const map = new Map<string, { top: number; height: number }>();
  let y = 4;
  panes.forEach((p, i) => {
    map.set(p.key, { top: y, height: p.height[size] });
    y += p.height[size] + (i < panes.length - 1 ? gap : 0);
  });
  return { left: size === 'phone' ? 26 : 34, font: size === 'phone' ? 10 : 11, height: y + BOTTOM, panes: map };
}

function buildOptions(
  panes: HistoryPane[],
  series: HistorySeries[],
  g: Geometry,
  initial: [number, number],
  minRange: number,
  handlers: React.MutableRefObject<ZoomHandlers>,
): Highcharts.Options {
  let tickLabels = new Map<number, { text: string; bold: boolean }>();
  return {
    chart: {
      height: g.height,
      marginLeft: g.left,
      marginRight: 4,
      marginTop: 4,
      marginBottom: BOTTOM,
      spacing: [0, 0, 0, 0],
      backgroundColor: 'transparent',
      animation: false,
      style: { fontFamily: 'inherit' },
      selectionMarkerFill: 'rgba(42,120,214,0.12)',
      zooming: { type: 'x', pinchType: 'x' },
      events: {
        load: hideHighchartsResetZoom,
        render() {
          nudgeEdgeLabels(this, g.font);
        },
      },
    },
    title: { text: undefined },
    credits: { enabled: false },
    legend: { enabled: false },
    tooltip: { enabled: false },
    time: TALLINN_TIME,
    xAxis: {
      type: 'datetime',
      min: initial[0],
      max: initial[1],
      minRange,
      lineColor: HAIRLINE_STRONG,
      tickLength: 0,
      tickPositioner() {
        const t = historyTicks(this.min ?? initial[0], this.max ?? initial[1]);
        tickLabels = t.labels;
        const info = {
          unitName: 'hour',
          unitRange: 3_600_000,
          count: 1,
          higherRanks: [],
          boundaryTicks: [] as string[],
          totalRange: 0,
        };
        return Object.assign(t.positions, { info });
      },
      labels: {
        y: g.font + 6,
        style: { fontSize: `${g.font}px`, color: MUTED, whiteSpace: 'nowrap', textOverflow: 'none' },
        formatter() {
          const l = tickLabels.get(this.value as number);
          return !l ? '' : l.bold ? `<span style="font-weight:700">${l.text}</span>` : l.text;
        },
      },
      events: {
        afterSetExtremes(e) {
          handlers.current.onExtremes(e.min, e.max);
        },
      },
    },
    yAxis: panes.map((p) => {
      const base = paneAxis(g.panes.get(p.key)!, g.font);
      return { ...base, ...p.axis, id: paneId(p.key), labels: { ...base.labels, ...p.axis.labels } };
    }),
    plotOptions: {
      series: {
        animation: false,
        lineWidth: 1.75,
        marker: { enabled: false },
        enableMouseTracking: false,
        states: { hover: { enabled: false }, inactive: { enabled: false } },
      },
    },
    series: series.map((s): Highcharts.SeriesOptionsType => {
      const o: Highcharts.SeriesOptionsType = {
        type: s.type ?? 'line',
        id: s.id,
        yAxis: paneId(s.pane),
        data: s.data,
        color: s.color,
        visible: s.visible ?? true,
        threshold: null,
      };
      // Only set what is given: an explicit undefined would override plotOptions (no line at all).
      return Object.assign(
        o,
        s.step && { step: s.step },
        s.lineWidth !== undefined && { lineWidth: s.lineWidth },
        s.fillOpacity !== undefined && { fillOpacity: s.fillOpacity },
      );
    }),
  };
}

export function HistoryCard(props: {
  size: LayoutSize;
  now: number;
  range: HistoryRange;
  onRange: (r: HistoryRange) => void;
  win: [number, number];
  panes: HistoryPane[];
  series: HistorySeries[];
  minRangeMs: number;
  chips: (t: number | null) => React.ReactNode; // t: the time under the pointer, null = now
  readout?: (t: number | null) => React.ReactNode;
  children?: React.ReactNode; // notes, legend, statistics under the chart
  style?: React.CSSProperties;
}): React.ReactElement {
  const { size, panes, series, win } = props;
  const phone = size === 'phone';
  const [chart, setChart] = useState<Highcharts.Chart | null>(null);
  const { sel, setSel, wrapRef, handlers: pointer } = useChartPointer(chart);
  const { zoom, resetZoom, handlers, winRef } = useWindowZoom(chart, win, () => setSel(null));
  const g = useMemo(() => geometry(size, panes), [size, panes]);
  const options = useMemo(
    () => buildOptions(panes, series, g, winRef.current, props.minRangeMs, handlers),
    [panes, series, g, props.minRangeMs],
  );
  const onChart = useCallback((c: Highcharts.Chart) => setChart(c), []);
  // A different set of panes is a different chart (heights, axes): remount instead of updating.
  const chartKey = `${size}:${panes.map((p) => p.key).join(',')}`;

  const pick = (r: HistoryRange) => {
    props.onRange(r);
    resetZoom();
    setSel(null);
  };
  const title = sel === null ? `Now · ${weekdayTime(props.now)}` : weekdayTime(sel, longRange(win[1] - win[0]));
  const ranges = (
    <Segmented
      label="Range"
      options={HISTORY_RANGES}
      value={zoom ? null : props.range}
      onPick={pick}
      size={size}
      stretch={phone}
    />
  );
  const reset = zoom && (
    <ResetZoomButton
      size={size}
      onClick={() => {
        resetZoom();
        setSel(null);
      }}
    />
  );
  const titleText = (
    <span
      aria-live="polite"
      className="num"
      style={{ fontSize: 12, fontWeight: 600, color: INK_SOFT, whiteSpace: 'nowrap' }}
    >
      {title}
    </span>
  );

  return (
    <Card
      label="History"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: phone ? 10 : 12,
        minWidth: 0,
        padding: phone ? '14px 12px 12px' : '16px 18px 14px',
        ...props.style,
      }}
    >
      {phone ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 2px', minHeight: 20 }}>
            <span style={{ fontSize: 14, fontWeight: 600, flex: 1 }}>History</span>
            {reset}
            {titleText}
          </div>
          {ranges}
        </>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 16px' }}>
          <span style={{ fontSize: 15, fontWeight: 600 }}>History</span>
          <span style={{ flex: 1, display: 'flex' }}>{titleText}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
            {reset}
            {ranges}
          </div>
        </div>
      )}
      {props.chips(sel)}
      {props.readout?.(sel)}
      <div ref={wrapRef} style={{ position: 'relative', cursor: 'crosshair' }} {...pointer}>
        <ChartView key={chartKey} options={options} onChart={onChart} updateArgs={UPDATE_ONE_TO_ONE} />
        {panes
          .filter((p) => p.title)
          .map((p) => (
            <PaneTitle key={p.key} left={g.left} top={g.panes.get(p.key)!.top} font={g.font}>
              {p.title}
            </PaneTitle>
          ))}
        <Crosshair chart={chart} x={sel} />
      </div>
      {props.children}
    </Card>
  );
}
